import React, { useEffect, useRef } from "react";

/**
 * Lightweight desktop-only gold cursor trail. Disabled on touch / reduced-motion.
 * Renders a fixed canvas overlay — does not affect layout.
 */
export const CursorTrail = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const isTouch =
      typeof window !== "undefined" &&
      (window.matchMedia("(pointer: coarse)").matches ||
        window.matchMedia("(hover: none)").matches);
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isTouch || prefersReduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    const points = [];
    const MAX = 18;

    const onMove = (e) => {
      points.push({ x: e.clientX, y: e.clientY, life: 1 });
      if (points.length > MAX) points.shift();
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    let raf;
    const render = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const r = (i / points.length) * 6 + 1.2;
        const alpha = p.life * (i / points.length) * 0.55;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 175, 55, ${alpha})`;
        ctx.shadowColor = "rgba(212,175,55,0.6)";
        ctx.shadowBlur = 8;
        ctx.fill();
        p.life *= 0.96;
      }
      ctx.shadowBlur = 0;
      // decay
      while (points.length && points[0].life < 0.05) points.shift();
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="cursor-trail"
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none z-[55] hidden md:block"
    />
  );
};
